import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@powersync/react";
import { runScore, sectionScore, type ChecklistPoint, type ChecklistResults, type ChecklistTemplateBody } from "@ect/contracts";
import { sq, startChecklistRun, saveChecklistResults } from "@ect/core";
import { Button, EmptyState, Input, ProgressRing } from "@ect/ui";
import { useSync } from "../app/powersync";

interface Tpl { id: string; name: string; name_ar: string | null; body: string }
interface Run { id: string; template_id: string; results: string; status: string }
const today = () => new Date().toISOString().slice(0, 10);
const parse = <T,>(s: string | null | undefined, fallback: T): T => { try { return s ? (JSON.parse(s) as T) : fallback; } catch { return fallback; } };

/** QHSE checklist by zone (sprint 6.5): % or OK/NOK/N/A per point, observations, section rings; saved locally, synced. */
export function QhseChecklist({ chantierId, entityId }: { chantierId: string; entityId: string | null }) {
  const { t, i18n } = useTranslation();
  const { db, actor } = useSync();
  const date = today();
  const { data: templates } = useQuery<Tpl>(sq.checklistTemplates().sql, sq.checklistTemplates().params);
  const { data: runs } = useQuery<Run>(sq.checklistRun(chantierId, date).sql, sq.checklistRun(chantierId, date).params);
  const run = runs[0] ?? null;
  const [templateId, setTemplateId] = useState<string>("");
  const tpl = templates.find((x) => x.id === (run?.template_id ?? templateId)) ?? templates[0];
  const body = useMemo(() => parse<ChecklistTemplateBody>(tpl?.body, { sections: [] }), [tpl]);
  const [results, setResults] = useState<ChecklistResults>({});
  useEffect(() => { setResults(parse<ChecklistResults>(run?.results, {})); }, [run?.id, run?.results]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const update = (code: string, patch: Partial<ChecklistResults[string]>) => {
    if (!run) return;
    const next = { ...results, [code]: { ...results[code], ...patch } };
    setResults(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void saveChecklistResults(db, run.id, next, run.status as "draft" | "done"), 600);
  };
  const score = runScore(body, results);
  const ar = i18n.language === "ar";
  if (!templates.length) return <EmptyState title={t("qhse.title")} description="Aucun modèle de checklist synchronisé" />;
  if (!run) {
    return (
      <div className="grid gap-4">
        <label className="grid gap-1 text-sm"><span className="text-ink-2">{t("qhse.template")}</span><select id="tpl" value={templateId || tpl?.id} onChange={(e) => setTemplateId(e.target.value)} className="h-11 rounded-md border border-line bg-surface px-3">{templates.map((x) => <option key={x.id} value={x.id}>{ar && x.name_ar ? x.name_ar : x.name}</option>)}</select></label>
        <Button size="lg" onClick={() => tpl && void startChecklistRun(db, actor, { chantierId, entityId, templateId: templateId || tpl.id, date })}>{t("qhse.start")}</Button>
      </div>
    );
  }
  const Point = ({ p, depth }: { p: ChecklistPoint; depth: number }) => {
    const r = results[p.code] ?? {};
    const leaf = !p.children?.length;
    return (
      <li className={`rounded-md border border-line bg-surface p-3 ${depth ? "ms-4" : ""}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium text-ink"><span className="tabular me-2 text-ink-3">{p.code}</span>{ar && p.label_ar ? p.label_ar : p.label_fr}</div>
          {leaf && p.mode === "percent" && r.pct != null && <span className="tabular font-display text-sm font-bold">{r.pct} %</span>}
        </div>
        {leaf && (
          <div className="mt-2 grid gap-2">
            {p.mode === "percent" ? (
              <div className="grid grid-cols-5 gap-1.5">{[0, 25, 50, 75, 100].map((v) => <button key={v} type="button" onClick={() => update(p.code, { pct: v })} className={`h-10 rounded-sm border text-xs font-semibold ${r.pct === v ? "border-navy bg-navy text-white" : "border-line bg-surface text-ink-2"}`}>{v}</button>)}</div>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">{(["pass", "fail", "na"] as const).map((v) => <button key={v} type="button" onClick={() => update(p.code, { check: v })} className={`h-10 rounded-sm border text-xs font-semibold ${r.check === v ? (v === "pass" ? "border-green bg-green text-white" : v === "fail" ? "border-danger bg-danger text-white" : "border-ink-3 bg-ink-3 text-white") : "border-line bg-surface text-ink-2"}`}>{t(`qhse.${v}`)}</button>)}</div>
            )}
            <Input id={`obs-${p.code}`} placeholder={t("qhse.observation")} value={r.observation ?? ""} onChange={(e) => update(p.code, { observation: e.target.value })} className="h-9 text-xs" />
          </div>
        )}
        {p.children?.length ? <ul className="mt-2 grid gap-2">{p.children.map((c) => <Point key={c.code} p={c} depth={depth + 1} />)}</ul> : null}
      </li>
    );
  };
  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-4 rounded-md border border-line bg-surface p-4">
        <ProgressRing value={score.score} size={72} />
        <div className="flex-1"><div className="font-display font-bold text-ink">{t("qhse.score")}</div><div className="text-xs text-ink-3">{t("qhse.evaluated", { n: score.evaluated, total: score.total })} · {ar && tpl?.name_ar ? tpl.name_ar : tpl?.name}</div></div>
        <Button variant={run.status === "done" ? "secondary" : "primary"} onClick={() => void saveChecklistResults(db, run.id, results, run.status === "done" ? "draft" : "done")}>{run.status === "done" ? t("qhse.reopen") : t("qhse.done")}</Button>
      </div>
      {body.sections.map((s) => {
        const sc = sectionScore(s, results);
        return (
          <section key={s.code}>
            <div className="mb-2 flex items-center justify-between"><h2 className="font-display text-sm font-bold uppercase tracking-wide text-ink-3">{ar && s.title_ar ? s.title_ar : s.title_fr}</h2><span className="tabular text-xs font-semibold text-ink-2">{sc.score == null ? "–" : `${sc.score} %`} · {sc.evaluated}/{sc.total}</span></div>
            <ul className="grid gap-2">{s.points.map((p) => <Point key={p.code} p={p} depth={0} />)}</ul>
          </section>
        );
      })}
    </div>
  );
}
